import { FC, useState } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { AvailablePaymentCurrency } from "@simplyk/common";

import { PRIZES_SECTION_ID } from "../helpers/constants";
import { TicketingPrizeOutput } from "../types";

import { DesktopPrizeList } from "./DesktopPrizeList/DesktopPrizeList";
import { MobilePrizeCarousel } from "./MobilePrizeCarousel/MobilePrizeCarousel";
import { PrizeDetailsDialog } from "./PrizeDetailsDialog/PrizeDetailsDialog";

import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
import { FormTypography } from "@/forms-design-system/FormTypography";

interface PrizesSectionProps {
  prizes: TicketingPrizeOutput[];
  currency: AvailablePaymentCurrency;
  initialSelectedIndex?: number;
  initialOpenedPrizeId?: string;
  isDialogInline?: boolean;
  focusedPrizeId?: string | null;
}

export const PrizesSection: FC<PrizesSectionProps> = ({
  prizes,
  currency,
  initialSelectedIndex = 0,
  initialOpenedPrizeId,
  isDialogInline = false,
  focusedPrizeId = null,
}) => {
  const { t } = useTranslate();
  const { isSmallScreen } = useMediaQuery();
  const [selectedIndex, setSelectedIndex] = useState(initialSelectedIndex);
  const focusedIndex = prizes.findIndex((prize) => prize.id === focusedPrizeId);
  const visibleSelectedIndex = focusedIndex !== -1 ? focusedIndex : Math.min(selectedIndex, prizes.length - 1);
  const [openedPrizeId, setOpenedPrizeId] = useState<string | null>(initialOpenedPrizeId ?? null);
  const openedPrize = prizes.find((prize) => prize.id === openedPrizeId) ?? null;
  const openPrize = (prize: TicketingPrizeOutput) => setOpenedPrizeId(prize.id);

  return (
    <Stack id={PRIZES_SECTION_ID} data-test={PRIZES_SECTION_ID} width="100%">
      <Box sx={{ paddingTop: { xs: 1, md: 0 }, paddingBottom: 1 }}>
        <FormTypography variant="h5" vibe="intense" component="h2">
          {t("ticketing", "prizes.title", { count: prizes.length })}
        </FormTypography>
      </Box>
      {isSmallScreen ? (
        <MobilePrizeCarousel
          prizes={prizes}
          currency={currency}
          selectedIndex={visibleSelectedIndex}
          onSelect={setSelectedIndex}
          onOpen={openPrize}
        />
      ) : (
        <DesktopPrizeList prizes={prizes} currency={currency} focusedPrizeId={focusedPrizeId} onOpen={openPrize} />
      )}
      <PrizeDetailsDialog
        prize={openedPrize}
        currency={currency}
        onClose={() => setOpenedPrizeId(null)}
        inline={isDialogInline}
      />
    </Stack>
  );
};
